import { motion, type Variants } from 'framer-motion'
import type { ReactNode } from 'react'

/* The landing's single entrance gesture. Every section reads these constants:
   if the gesture changes, it changes here and nowhere else. */
const DURATION = 0.65
/* Sharp deceleration — it arrives and settles, no bounce. */
const EASE: [number, number, number, number] = [0.22, 1, 0.36, 1]
const RISE = 16
const STAGGER = 0.14

/* `once: true`: each block animates on its first appearance then stays put —
   replaying on every pass gets aggressive on a page read top to bottom.
   `amount: 0.2`: the gesture fires once 20% of the block is visible. */
export const REVEAL_VIEWPORT = { once: true, amount: 0.2 } as const

const TRANSITION = { duration: DURATION, ease: EASE }

const revealVariants: Variants = {
  hidden: { opacity: 0, y: RISE },
  show: { opacity: 1, y: 0, transition: TRANSITION },
}

/* Fade only, no rise: for anything the rise would spoil rather than announce —
   an image that would overflow its band, a marquee already scrolling. Each
   caller picks one of the two. */
export const fadeVariants: Variants = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: TRANSITION },
}

/* The group animates nothing itself: its variants are empty, it only paces its
   children. Hence no `data-reveal` — it writes no opacity for the `<noscript>`
   safety net to undo. */
const groupVariants: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: STAGGER } },
}

type RevealItemProps = {
  children: ReactNode
  className?: string
}

type RevealProps = RevealItemProps & {
  fade?: boolean
}

/** A block that animates on its own once it enters the viewport. */
export function Reveal({ children, className, fade = false }: RevealProps) {
  return (
    <motion.div
      data-reveal
      className={className}
      variants={fade ? fadeVariants : revealVariants}
      initial="hidden"
      whileInView="show"
      viewport={REVEAL_VIEWPORT}
    >
      {children}
    </motion.div>
  )
}

type RevealGroupProps = {
  children: ReactNode
  className?: string
  /** `'mount'` for the hero, already visible on load: it must not wait for a
      scroll that will never come. */
  trigger?: 'scroll' | 'mount'
}

/** Paces its `RevealItem` children. Variants propagate on their own: the items
    have no trigger of their own. */
export function RevealGroup({
  children,
  className,
  trigger = 'scroll',
}: RevealGroupProps) {
  const scroll = trigger === 'scroll'
  return (
    <motion.div
      className={className}
      variants={groupVariants}
      initial="hidden"
      animate={scroll ? undefined : 'show'}
      whileInView={scroll ? 'show' : undefined}
      viewport={scroll ? REVEAL_VIEWPORT : undefined}
    >
      {children}
    </motion.div>
  )
}

/** A child of `RevealGroup`. Outside a group it stays invisible. */
export function RevealItem({ children, className }: RevealItemProps) {
  return (
    <motion.div data-reveal className={className} variants={revealVariants}>
      {children}
    </motion.div>
  )
}
